import { useEffect, useCallback, useRef } from "react";


export function useUsdtSocket({
  socket,
  chatId,

  setUsdtAddress,
  setUsdtBalance,
  setUsdtHistory,

  onAdCreated,
  onUsdtCommitted,
  onAdError,

  onMyAdFetched,
  onMyAdFetchError,

  onAdUpdated,
  onAdUpdateError,

  onAdCancelled,
  onAdCancelError,

  onOrderCreated,
  onOrderError,

  onSendSuccess,
  onSendError,

  showToast,
}) {
  const ensureUsdtAccount = useCallback(() => {
    if (!socket) return;
    socket.emit("usdt:get-address");
  }, [socket]);

  const requestUsdtAddress = useCallback(() => {
    if (!socket) return;
    socket.emit("usdt:get-address");
  }, [socket]);

  const requestUsdtBalance = useCallback(() => {
    if (!socket) return;
    socket.emit("usdt:get-balance");
  }, [socket]);

  const sendUsdtInternal = useCallback(
    async (payload) => {

      if (!socket) return;

      try {

        socket.emit(
          "usdt:send-internal",
          payload
        );

      } catch (err) {

        showToast?.(
          err.message,
          "error"
        );

      }

    },
    [
      socket,
      chatId,
      showToast,
    ]
  );

  const sendUsdt = useCallback(
    async (payload) => {

      if (!socket) return;

      try {

        socket.emit(
          "usdt:send",
          payload
        );

      } catch (err) {

        showToast?.(
          err.message,
          "error"
        );

      }

    },
    [
      socket,
      chatId,
      showToast,
    ]
  );

  const createUsdtAd = useCallback(
  async (payload) => {

    if (!socket) return;

    try {

      socket.emit(
        "usdt:create-ad",
        payload
      );

    } catch (err) {

      showToast?.(
        err.message,
        "error"
      );

    }

  },
  [
    socket,
    showToast,
  ]
);

  const getMyUsdtAd = useCallback(
    async () => {

      if (!socket) return;

      try {

        socket.emit("usdt:get-my-ad");

      } catch (err) {

        showToast?.(
          err.message,
          "error"
        );

      }

    },
    [
      socket,
      showToast,
    ]
  );

  // payload shape (built by the transaction-building module, not here):
  // { adId, rate, minLimit, maxLimit, newAmount, fromAddress?, signedTransaction?, lastValidBlockHeight? }
  // fromAddress/signedTransaction/lastValidBlockHeight are only required
  // when newAmount increases the ad's committed amount — the caller
  // decides whether a transaction needs to be built before calling this.
  const updateUsdtAd = useCallback(
    async (payload) => {

      if (!socket) return;

      try {

        socket.emit(
          "usdt:update-ad",
          payload
        );

      } catch (err) {

        showToast?.(
          err.message,
          "error"
        );

      }

    },
    [
      socket,
      showToast,
    ]
  );

  // payload shape: { adId } — backend builds + signs the entire
  // refund transaction itself (custodial key is platform-held),
  // so no transaction-building step is needed on the caller's end.
  const cancelUsdtAd = useCallback(
    async (payload) => {

      if (!socket) return;

      try {

        socket.emit(
          "usdt:cancel-ad",
          payload
        );

      } catch (err) {

        showToast?.(
          err.message,
          "error"
        );

      }

    },
    [
      socket,
      showToast,
    ]
  );

  const createUsdtOrder = useCallback(
    async (payload) => {

      if (!socket) return;

      try {

        socket.emit(
          "usdt:create-order",
          payload
        );

      } catch (err) {

        showToast?.(
          err.message,
          "error"
        );

      }

    },
    [
      socket,
      chatId,
      showToast,
    ]
  );

  const initialFetchDoneRef = useRef(false);

  useEffect(() => {
    if (!socket) return;
    if (initialFetchDoneRef.current) return;
    initialFetchDoneRef.current = true;
    socket.emit("usdt:get-address");
    socket.emit("usdt:get-balance");
  }, [socket]);

  useEffect(() => {
    if (!socket) return;


    const onAddress = (payload) => {
      setUsdtAddress?.(payload?.address ?? null);
    };

    const onBalance = (payload) => {

  if (payload?.address) {
    setUsdtAddress?.(payload.address);
  }

  setUsdtBalance?.(payload);

};
    const onHistory = (payload) => {
      setUsdtHistory?.((prev) => [
        payload,
        ...(prev || []),
      ]);
    };

    const onAdCreatedSuccess = (payload) => {
      socket.emit("usdt:get-balance");

      onAdCreated?.({
        ad:        payload?.ad || payload,
        committed: payload?.committed,
        balance:   payload?.balance,
        available: payload?.available,
      });
    };

    // Handles the broadcast received by ALL other connected users
    const onUsdtAdCreated = (ad) => {
      onAdCreated?.({
        ad,
        fromBroadcast: true,
      });
    };

    const onAdCreatedError = (payload) => {
      const msg = payload?.message || "USDT ad creation failed";
      showToast?.(msg, "error");
      onAdError?.(msg);
    };

const onMyAdSuccess = (payload) => {
  onMyAdFetched?.(payload);
};
    const onMyAdError = (payload) => {
      const msg = payload?.message || "Failed to load advertisement";
      showToast?.(msg, "error");
      onMyAdFetchError?.(msg);
    };

    const onAdUpdatedSuccess = (payload) => {
  socket.emit("usdt:get-balance");
  onAdUpdated?.(payload);
};

// Handles the broadcast received by ALL other connected users
const onUsdtAdUpdatedBroadcast = (ad) => {
  if (ad?.status === "cancelled") {
    onAdCancelled?.({ ad, fromBroadcast: true });
    return;
  }

  onAdUpdated?.({
    ad,
    fromBroadcast: true,
  });
};

    const onAdUpdatedError = (payload) => {
      const msg = payload?.message || "USDT ad update failed";
      showToast?.(msg, "error");
      onAdUpdateError?.(msg);
    };

    const onAdCancelledSuccess = (payload) => {
      // usdt:cancel-ad reuses the "usdt:ad-updated" broadcast (the ad's
      // status flips to "cancelled"), so onUsdtAdUpdatedBroadcast above
      // already keeps other clients in sync — this only handles the
      // caller's own success path.
      socket.emit("usdt:get-balance");
      onAdCancelled?.(payload);
    };

    const onAdCancelledError = (payload) => {
      const msg = payload?.message || "USDT ad cancellation failed";
      showToast?.(msg, "error");
      onAdCancelError?.(msg);
    };

    const onOrderCreatedSuccess = (payload) => {
  onOrderCreated?.({
    ...payload,
    settlementAsset: payload.settlementAsset,
    settlementAmount: payload.settlementAmount,
  });
};
    const onOrderCreatedError = (payload) => {
      const msg = payload?.message || "USDT order failed";
      showToast?.(msg, "error");
      onOrderError?.(msg);
    };

    const onInternalSendSuccess = (payload) => {
      const amountText =
        payload?.amount !== undefined
          ? `${Number(payload.amount).toFixed(2)} USDT `
          : "USDT ";
      showToast?.(`${amountText}sent to ${payload.toEmail}`, "success");
      socket.emit("usdt:get-balance");

      // Tell SendUsdtInternal the transfer completed — mirrors
      // handleSendSuccess below for the on-chain flow. Without this,
      // SendUsdtInternal never learns the send succeeded (its success
      // screen never appears) even though the balance refresh above
      // still happens.
      onSendSuccess?.current?.(payload);
    };

    const onInternalSendError = (payload) => {
      showToast?.(payload?.message, "error");

      // Tell SendUsdtInternal the transfer failed, mirroring
      // handleSendError below for the on-chain flow.
      onSendError?.current?.(payload);
    };

    const handleSendSuccess = (payload) => {

      const feeText =
        payload?.fee
          ? ` (Fee: ${payload.fee} USDT)`
          : "";

      showToast?.(
        `USDT sent successfully${feeText}`,
        "success"
      );

      socket.emit("usdt:get-balance");

      // Tell UsdtSend the transfer completed
      onSendSuccess?.current?.(payload);
    };

    const handleSendError = (payload) => {

      showToast?.(
        payload?.message ||
        "USDT on-chain send failed",
        "error"
      );

      // Tell UsdtSend the transfer failed
      onSendError?.current?.(payload);
    };

    const onGenericError = (payload) => {
      showToast?.(payload?.message || "USDT system error", "error");
    };

    /* ---------- BIND ---------- */
    socket.on("usdt:address",              onAddress);
    socket.on("usdt:balance",              onBalance);
    socket.on("usdt:history",              onHistory);

    socket.on("usdt:create-ad:success",    onAdCreatedSuccess);
    socket.on("usdt:create-ad:error",      onAdCreatedError);
    socket.on("usdt:ad-created",           onUsdtAdCreated);

    socket.on("usdt:get-my-ad:success",    onMyAdSuccess);
    socket.on("usdt:get-my-ad:error",      onMyAdError);

    socket.on("usdt:update-ad:success",    onAdUpdatedSuccess);
    socket.on("usdt:ad-updated",        onUsdtAdUpdatedBroadcast); 
    socket.on("usdt:update-ad:error",      onAdUpdatedError);

    socket.on("usdt:cancel-ad:success",    onAdCancelledSuccess);
    socket.on("usdt:cancel-ad:error",      onAdCancelledError);

    socket.on("usdt:create-order:success", onOrderCreatedSuccess);
    socket.on("usdt:create-order:error",   onOrderCreatedError);

    socket.on("usdt:send-success",         onInternalSendSuccess);
    socket.on("usdt:send-internal:error",  onInternalSendError);

    socket.on("usdt:send:success",         handleSendSuccess);
    socket.on("usdt:send:error",           handleSendError);

    socket.on("usdt:error",                onGenericError);

    return () => {
      socket.off("usdt:address",              onAddress);
      socket.off("usdt:balance",              onBalance);
      socket.off("usdt:history",              onHistory);

      socket.off("usdt:create-ad:success",    onAdCreatedSuccess);
      socket.off("usdt:create-ad:error",      onAdCreatedError);
      socket.off("usdt:ad-created",           onUsdtAdCreated);

      socket.off("usdt:get-my-ad:success",    onMyAdSuccess);
      socket.off("usdt:get-my-ad:error",      onMyAdError);

      socket.off("usdt:update-ad:success",    onAdUpdatedSuccess);
      socket.off("usdt:ad-updated",        onUsdtAdUpdatedBroadcast);
      socket.off("usdt:update-ad:error",      onAdUpdatedError);

      socket.off("usdt:cancel-ad:success",    onAdCancelledSuccess);
      socket.off("usdt:cancel-ad:error",      onAdCancelledError);

      socket.off("usdt:create-order:success", onOrderCreatedSuccess);
      socket.off("usdt:create-order:error",   onOrderCreatedError);

      socket.off("usdt:send-success",         onInternalSendSuccess);
      socket.off("usdt:send-internal:error",  onInternalSendError);

      socket.off("usdt:send:success",         handleSendSuccess);
      socket.off("usdt:send:error",           handleSendError);

      socket.off("usdt:error",                onGenericError);
    };
  }, [
    socket,
    setUsdtAddress,
    setUsdtBalance,
    setUsdtHistory,
    showToast,
    onAdCreated,
    onAdError,
    onMyAdFetched,
    onMyAdFetchError,
    onAdUpdated,
    onAdUpdateError,
    onAdCancelled,
    onAdCancelError,
    onOrderCreated,
    onOrderError,
    onSendSuccess,
    onSendError,
  ]);

  return {
    ensureUsdtAccount,
    requestUsdtAddress,
    requestUsdtBalance,

    createUsdtAd,
    getMyUsdtAd,
    updateUsdtAd,
    cancelUsdtAd,

    createUsdtOrder,

    sendUsdt,
    sendUsdtInternal,
  };
}